import {
  CreateFormAuditDto,
  FormBaseType,
  FormBasicsDto,
  FormState,
  UserRole,
} from "@green0meter/api-client";
import React, { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import FeatherIcon from "feather-icons-react";
import { useTranslation } from "react-i18next";
import { enqueueSnackbar } from "notistack";
import dayjs from "dayjs";

import FormBasicsDialog from "../FormBasicsDialog";
import RemoveFormDialog from "./components/RemoveFormDialog";

import { CardTableColumn, CardTable, CardTableRow } from "@components/common";
import { useCreateFormAudit, useLockForm, useRemoveForm, useUnlockForm } from "@api";
import { Stack, Tooltip, IconButton, Chip, Box, Button } from "@components/base";
import { useUserStore } from "@store/user";
import { formatDateUtc } from "@helpers";

interface CollectDataTable {
  forms: FormBasicsDto[];
  formBaseType: FormBaseType;
  isFetchingAvailableForms: boolean;
  isLockDeleteActionsDisabled?: boolean;
  procurementId?: string;
}

const CollectDataTable: FC<CollectDataTable> = ({
  forms,
  formBaseType,
  isFetchingAvailableForms,
  isLockDeleteActionsDisabled = false,
  procurementId,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [newFormDialogOpen, setNewFormDialogOpen] = useState(false);
  const [removeForm, setRemoveForm] = useState<FormBasicsDto>();
  const [rows, setRows] = useState<CardTableRow[]>([]);
  const { user } = useUserStore();
  const { lockForm, lockFormEnabled } = useLockForm(user);
  const { unlockForm, unlockFormEnabled } = useUnlockForm(user);
  const { removeFormEnabled } = useRemoveForm(user);
  const { createFormAudit, createFormAuditEnabled } = useCreateFormAudit(user);

  const columns: CardTableColumn[] = [
    {
      field: "name",
      title: t("common.formName"),
    },
    {
      field: "reportingPeriod",
      title: t("common.reportingPeriod"),
    },
    {
      field: "status",
      title: t("common.status"),
    },
    {
      field: "deadline",
      title: t("common.deadline"),
    },
    {
      field: "actions",
      title: t("common.actions"),
      tableProps: {
        align: "right",
      },
    },
  ];

  const getFormUrl = (
    formType: FormBaseType,
    formId: string,
    procurementId: string,
  ): string => {
    const formUrls: Record<number, string> = {
      [FormBaseType.Co2]: `/co2/form/${formId}`,
      [FormBaseType.VSME]: `/vsme/form/${formId}`,
      [FormBaseType.ESRS]: `/csrd/form/${formId}`,
      [FormBaseType.BRSR]: `/brsr/form/${formId}`,
      [FormBaseType.CsobSk]: `/csob-sk/form/${formId}`,
      [FormBaseType.ProcurementSupplier]: `/suppliers-hub/${procurementId}/form/${formId}`,
    };

    return formUrls[formType] || `/form/${formId}`;
  };

  const handleEdit = (formId: string): void => {
    navigate(getFormUrl(formBaseType, formId, procurementId ?? ""));
  };

  const handleLock = (formId: string, isLocked: boolean): void => {
    if (isLocked) {
      unlockForm(formId);
    } else {
      lockForm(formId);
    }
  };

  const handleNewFormDialogOpen = (): void => {
    setNewFormDialogOpen(true);
  };

  const handleNewFormDialogClose = (): void => {
    setNewFormDialogOpen(false);
  };

  const handleRemoveFormDialogOpen = (form: FormBasicsDto): void => {
    setRemoveForm(form);
  };

  const handleCreateFormAudit = (form: FormBasicsDto): void => {
    const dto: CreateFormAuditDto = {
      formId: form.id,
    };

    createFormAudit(dto, {
      onSuccess: () => {
        enqueueSnackbar(t("common.auditFileBeingCreated"), { variant: "info" });
      },
      onError: () => {
        enqueueSnackbar(t("common.auditFileNotCreated"), { variant: "error" });
      },
    });
  };

  const handleRemoveFormDialogClose = (): void => {
    setRemoveForm(undefined);
  };

  useEffect(() => {
    if (forms && forms.length) {
      setRows(
        forms.map((form) => {
          const now = dayjs();
          const deadline = form?.deadline ? dayjs(form.deadline) : null;
          const isDeadlinePassed = deadline ? now.isAfter(deadline, "day") : false;
          const isLocked = isDeadlinePassed ? true : form.isLocked;

          return {
            name:
              formBaseType === FormBaseType.ProcurementSupplier
                ? `${form.name} ${t("common.supplier.defaultFormName")}`.trim()
                : form.name,
            reportingPeriod: `${form.validFrom ? formatDateUtc(form.validFrom) : ""} - ${
              form.validTo ? formatDateUtc(form.validTo) : ""
            }`,
            status:
              form.state === FormState.Submitted ? (
                <Chip
                  label={t("common.submitted")}
                  size="medium"
                  color="success"
                  variant="filled"
                />
              ) : isDeadlinePassed ? (
                <Chip
                  label={t("common.expired")}
                  size="medium"
                  color="error"
                  variant="filled"
                />
              ) : (
                <Chip
                  label={t("common.inProgress")}
                  size="medium"
                  color="warning"
                  variant="filled"
                />
              ),
            deadline: form?.deadline ? formatDateUtc(form.deadline) : "-",
            actions: (
              <Box textAlign="right">
                <Box display="inline-block">
                  {formBaseType === FormBaseType.ProcurementSupplier ? (
                    <Stack spacing={1} direction="row">
                      <Button
                        size="small"
                        onClick={() => handleEdit(form.id)}
                        color="primary"
                        variant="contained"
                      >
                        {t("common.edit")}
                      </Button>
                    </Stack>
                  ) : (
                    <Stack spacing={1} direction="row">
                      <Tooltip title={t("common.edit")} placement="top">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(form.id)}
                          color="primary"
                        >
                          <FeatherIcon icon="edit-3" size={16} />
                        </IconButton>
                      </Tooltip>

                      {lockFormEnabled && unlockFormEnabled && (
                        <Tooltip
                          title={t(isLocked ? "common.unlock" : "common.lock")}
                          placement="top"
                        >
                          <IconButton
                            disabled={isLockDeleteActionsDisabled}
                            size="small"
                            onClick={() => handleLock(form.id, isLocked)}
                            color="primary"
                          >
                            {isLocked ? (
                              <FeatherIcon icon="lock" size={16} />
                            ) : (
                              <FeatherIcon icon="unlock" size={16} />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}

                      {createFormAuditEnabled && (
                        <Tooltip title="Audit" placement="top">
                          <IconButton
                            disabled={
                              form.state !== FormState.Submitted ||
                              !form.isCalculationReady
                            }
                            size="small"
                            onClick={() => handleCreateFormAudit(form)}
                            color="primary"
                          >
                            <FeatherIcon icon="file-plus" size={16} />
                          </IconButton>
                        </Tooltip>
                      )}

                      {removeFormEnabled && (
                        <Tooltip title={t("common.delete")} placement="top">
                          <IconButton
                            disabled={isLockDeleteActionsDisabled}
                            size="small"
                            onClick={() => handleRemoveFormDialogOpen(form)}
                            color="error"
                          >
                            <FeatherIcon icon="trash-2" size={16} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  )}
                </Box>
              </Box>
            ),
          };
        }),
      );
    } else {
      setRows([]);
    }
  }, [
    forms,
    lockFormEnabled,
    unlockFormEnabled,
    removeFormEnabled,
    createFormAuditEnabled,
  ]);

  return (
    <>
      <CardTable
        type="collect-data"
        title={t("vsme.hub.dataCollectionForms")}
        columns={columns}
        rows={rows}
        isLoaded={!isFetchingAvailableForms}
        button={
          formBaseType === FormBaseType.VSME || formBaseType === FormBaseType.ESRS
            ? {
                text: t("common.newForm"),
                disabled:
                  user?.role === UserRole.Auditor ||
                  user?.role === UserRole.Manager ||
                  user?.role === UserRole.Respondent,
                onClick: handleNewFormDialogOpen,
                variant: "contained",
              }
            : undefined
        }
      />
      <FormBasicsDialog
        open={newFormDialogOpen}
        formBaseType={formBaseType}
        onClose={handleNewFormDialogClose}
      />
      {removeForm && (
        <RemoveFormDialog form={removeForm} onClose={handleRemoveFormDialogClose} />
      )}
    </>
  );
};

export default CollectDataTable;
